import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, Box, Group, Menu, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconBuilding, IconChevronDown, IconHistory, IconLogout, IconUser } from '@tabler/icons-react';
import IdText from '@/components/common/IdText';
import { useProfile } from '@/hooks/useProfile';
import { formatOrgName, orgInitials } from '@/lib/format';
import { useAuth } from '@/stores/authContext';
import { ROUTES } from '@/routes/paths';
import type { UserProfile } from '@/types/auth';
import classes from './AccountMenu.module.css';

function initials(profile?: UserProfile): string {
  if (!profile) {
    return '';
  }
  if (profile.firstName || profile.lastName) {
    return `${profile.firstName?.[0] ?? ''}${profile.lastName?.[0] ?? ''}`.toUpperCase();
  }
  if (profile.name) {
    return orgInitials(profile.name);
  }
  return (profile.email?.[0] ?? '?').toUpperCase();
}

/** Пункты аккаунта: один список для меню в шапке и мобильного меню. */
export const ACCOUNT_LINKS = [
  { to: ROUTES.APP_COMPANY, key: 'app.account.company', Icon: IconBuilding },
  { to: ROUTES.APP_PROFILE, key: 'app.account.profile', Icon: IconUser },
  { to: ROUTES.APP_AUDIT, key: 'app.account.audit', Icon: IconHistory },
] as const;

/** Подпись аккаунта: организация с ИНН и email. Владелец всех данных — организация, её надо видеть. */
export function AccountIdentity({ profile }: { readonly profile?: UserProfile }) {
  const { t } = useTranslation();
  if (!profile) {
    return null;
  }
  return (
    <Stack gap={2}>
      {profile.name ? (
        <Text size="sm" fw={600} lineClamp={2}>
          {formatOrgName(profile.name)}
        </Text>
      ) : null}
      {profile.inn ? (
        <Text size="sm" c="gray.7">
          {t('auth.fields.inn')} <IdText>{profile.inn}</IdText>
        </Text>
      ) : null}
      {profile.email ? (
        <Text size="sm" c="gray.7" truncate>
          {profile.email}
        </Text>
      ) : null}
    </Stack>
  );
}

// Меню аккаунта в шапке кабинета: организация и email, разделы аккаунта, выход.
export default function AccountMenu() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { data: profile, isLoading } = useProfile();

  const label = profile?.email ?? (isLoading ? '…' : '');

  function handleLogout(): void {
    logout();
    navigate(ROUTES.LOGIN, { replace: true });
  }

  return (
    <Menu position="bottom-end" width={270} withinPortal>
      <Menu.Target>
        <UnstyledButton aria-label={`${t('app.account.menu')}: ${label}`}>
          <Group gap={8} wrap="nowrap">
            <Avatar size={30} radius="xl" color="brand">
              {initials(profile)}
            </Avatar>
            <Text size="sm" fw={500} maw={160} truncate>
              {label}
            </Text>
            <IconChevronDown size={16} />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Box px="sm" py="xs">
          <AccountIdentity profile={profile} />
        </Box>
        <Menu.Divider />
        {ACCOUNT_LINKS.map(({ to, key, Icon }) => (
          <Menu.Item key={to} className={classes.greenItem} component={Link} to={to} leftSection={<Icon size={16} />}>
            {t(key)}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item color="red.9" leftSection={<IconLogout size={16} />} onClick={handleLogout}>
          {t('nav.logout')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
